/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';

import _ from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { Box, Grid } from '@mui/material';

import OhsCusvalFieldFactory from './OhsCusvalFieldFactory';
import { InvalidCusvalTypes, OhsCusval, OhsCusvalType } from './OhsCusvalModel';
import { setCusvalValueToForm, returnInvalidCusvals } from './OhsCusvalServices';
import { OhsFormLayoutType, OhsFormSettings } from '../services/OhsFormModels';
import { RadioOptions } from '../fields/OhsRadioField';
import { SelectFieldOptionType } from '../fields/OhsSelectField';

interface Props {
  id: string;
  cusvalId: string;
  ohsFormSettings: OhsFormSettings;
  gridOptions?: any;

  categoryName: string;
  // initial value of category/subcategory
  initRecordValue?: string;
  // initial category options for checking whether cusvals still belongs to existing category or not
  initialOptions?: Array<SelectFieldOptionType>;

  cusvalsConfig: OhsCusval[] | null;
  isInitCusvalConfigFetched: boolean;
}

const useStyles = makeStyles()(() => ({
  invalidContainer: {
    position: 'relative',
    width: '100%',
  },
  invalidOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#9f3a38',
    opacity: 0.05,
  },
  invalidMessage: {
    color: '#9f3a38',
    fontSize: '12px',
  },
  container: {
    width: '100%',
  },
}));

export default function OhsCategoryCusvalFieldsSectionContent(props: Props) {
  const { classes } = useStyles();

  const [initialCusval, setInitialCusval] = useState<OhsCusval[] | null>(null); // prop cusvals before being formatted to form obj
  const [initialCusvalId, setInitialCusvalId] = useState(''); // store first cusval id when editing record, used for initial cusvals for potential deletion handling
  const [isFormatted, setIsFormatted] = useState<boolean>(false); // if prop cusvals is formatted to form obj
  const [invalidCusvals, setInvalidCusval] = useState<OhsCusval[]>([]); // when cusvals are deleted from a category but still has values
  const [invalidIsInitialized, setInvalidIsInitialized] = useState<boolean>(false);
  const [cusvalsFromDeletedCategory, setCusvalsFromDeletedCategory] = useState<OhsCusval[]>([]);
  const [storedCusvalValues, setStoredCusvalValues] = useState<any>({});

  const memoizedCusvalId: string = useMemo(() => props.cusvalId, [props.cusvalId]);
  const ohsFormSettings = new OhsFormSettings({}, props.ohsFormSettings.layout);
  const propValues = props.ohsFormSettings.useFormMethods.getValues();
  const cusvalPropValues = propValues[props.id] || [];
  const formValues = ohsFormSettings.useFormMethods.watch(props.id);

  const formSubmit = () => {};

  props.ohsFormSettings.useFormMethods.register(props.id, {
    validate: {
      noErrors: async () => {
        const withError = await ohsFormSettings.useFormMethods
          .handleSubmit(formSubmit)()
          .then(() => {
            const error = _.get(ohsFormSettings.useFormMethods.formState.errors, props.id);
            if (!_.isUndefined(error) && !_.isEmpty(error) && !_.isNil(error)) {
              return true;
            }
            return false;
          });
        return !withError;
      },
    },
  });

  const returnCusval = (item: OhsCusval, index: number, isInvalid?: boolean) => {
    let additionalProps = {};
    if (isInvalid) {
      if (item.type === OhsCusvalType.Currency) {
        const options: string[] = [item.value?.code || ''];
        additionalProps = { ...additionalProps, options };
      }
      if (item.type === OhsCusvalType.Radio || item.type === OhsCusvalType.Checkbox) {
        let options: RadioOptions = [];
        (item.value || []).forEach((val: string) => {
          options = [...options, { name: val, value: val }];
        });
        additionalProps = { ...additionalProps, options };
      }
      if (item.type === OhsCusvalType.Select || item.type === OhsCusvalType.MutipleSelect) {
        let options: Array<any> = [];
        (item.value || []).forEach((val: string) => {
          options = [...options, { name: val[0] }];
        });
        additionalProps = { ...additionalProps, options };
      }
    }

    return (
      <Grid
        key={index}
        item
        xs={12}
        sm={
          ohsFormSettings.layout === OhsFormLayoutType.AngularModalForm &&
          (item.type === OhsCusvalType.Text ||
            item.type === OhsCusvalType.Color ||
            item.type === OhsCusvalType.Date ||
            item.type === OhsCusvalType.Time ||
            item.type === OhsCusvalType.Url ||
            item.type === OhsCusvalType.Email ||
            item.type === OhsCusvalType.Number)
            ? 4
            : false
        }
      >
        <OhsCusvalFieldFactory
          key={index}
          sectionId={props.id}
          cusval={{
            ...item,
            ...additionalProps,
          }}
          ohsFormSettings={ohsFormSettings}
        />
      </Grid>
    );
  };

  // display invalid cusvals
  const renderInvalidCusvals = (cusvalsToDisplay: OhsCusval[], type: InvalidCusvalTypes) => {
    if (cusvalsToDisplay.length < 1) return '';
    const initialCategoryName = (props.initialOptions || []).find(
      (item) => item.value === props.initRecordValue
    );
    const renderMessage = () => {
      switch (type) {
        case InvalidCusvalTypes.DeletedCategory:
          return (
            <>
              <Box className={classes.invalidOverlay} />
              <Box pt={1} px={1} className={classes.invalidMessage}>
                <b>Note:</b> {cusvalsToDisplay.length > 1 ? 'These fields' : 'This field'} belongs
                to a {props.categoryName} that has been removed, associated data will be deleted if
                you update/edit
              </Box>
            </>
          );

        case InvalidCusvalTypes.InvalidCusval:
          return (
            <>
              <Box className={classes.invalidOverlay} />
              <Box pt={1} px={1} className={classes.invalidMessage}>
                <b>Note:</b> {cusvalsToDisplay.length > 1 ? 'These fields have' : 'This field has'}{' '}
                been removed, associated data will be deleted if you update/edit
              </Box>
            </>
          );
        case InvalidCusvalTypes.InitialCusvalForDeletion:
          return (
            <>
              <Box className={classes.invalidOverlay} />
              <Box pt={1} px={1} className={classes.invalidMessage}>
                <b>Note:</b> {cusvalsToDisplay.length > 1 ? 'These fields' : 'This field'} belongs
                to {props.categoryName} &quot;{initialCategoryName?.name}&quot;, associated data
                will be deleted if you update/edit
              </Box>
            </>
          );
        default:
          return <div />;
      }
    };

    if (ohsFormSettings.layout === OhsFormLayoutType.AngularPageForm) {
      return (
        <Box py={1} className={classes.invalidContainer}>
          {(cusvalsToDisplay || []).map((item, index) => {
            const cusvalItem = {
              ...item,
              disabled: true,
              required: false,
            };
            return returnCusval(cusvalItem, index, true);
          })}
          {renderMessage()}
        </Box>
      );
    }
    return (
      <Box ml={3} mt={3} className={classes.invalidContainer}>
        {(cusvalsToDisplay || []).map((item, index) => {
          const cusvalItem = {
            ...item,
            disabled: true,
            required: false,
          };
          return returnCusval(cusvalItem, index, true);
        })}
        {renderMessage()}
      </Box>
    );
  };

  const displayDeletedAndInvalidCusvals = () => {
    // Prevent invalid cusval from displaying deleted category cusval in a split second
    const displayedInvalidCusval = invalidCusvals.filter(
      (item) => !cusvalsFromDeletedCategory.some((i) => i._id === item._id)
    );

    const displayedForDeletionCusvals = (initialCusval || []).filter(
      (item) =>
        ![...cusvalsFromDeletedCategory, ...displayedInvalidCusval].some((i) => i._id === item._id)
    );
    return (
      <>
        {/* Cusvals for deletion when the category it belongs to is deleted */}
        {cusvalsFromDeletedCategory.length > 0 && (
          <Box
            className={classes.container}
            data-testid={`${props.id}-${InvalidCusvalTypes.DeletedCategory}`}
          >
            {renderInvalidCusvals(cusvalsFromDeletedCategory, InvalidCusvalTypes.DeletedCategory)}
          </Box>
        )}
        {/* Cusvals for deletion when they're deleted from config */}
        {displayedInvalidCusval.length > 0 && (
          <Box
            className={classes.container}
            data-testid={`${props.id}-${InvalidCusvalTypes.InvalidCusval}`}
          >
            {renderInvalidCusvals(displayedInvalidCusval, InvalidCusvalTypes.InvalidCusval)}
          </Box>
        )}
        {/* Cusvals for deletion from record when category is changed */}
        {initialCusvalId &&
          initialCusvalId !== memoizedCusvalId &&
          displayedForDeletionCusvals.length > 0 && (
            <Box
              className={classes.container}
              data-testid={`${props.id}-${InvalidCusvalTypes.InitialCusvalForDeletion}`}
            >
              {renderInvalidCusvals(
                displayedForDeletionCusvals,
                InvalidCusvalTypes.InitialCusvalForDeletion
              )}
            </Box>
          )}
      </>
    );
  };

  // handle invalid cusvals
  useEffect(() => {
    // fill invalid cusval state only once
    if (
      !invalidIsInitialized &&
      initialCusval &&
      props.cusvalsConfig &&
      props.isInitCusvalConfigFetched
    ) {
      const newInvalidCusvals = returnInvalidCusvals(initialCusval, props.cusvalsConfig);
      setInvalidCusval(newInvalidCusvals);
      setInvalidIsInitialized(true);
    }
  }, [
    initialCusval,
    props.cusvalsConfig,
    cusvalsFromDeletedCategory,
    props.isInitCusvalConfigFetched,
  ]);

  // prevent cusvalsFromDeletedCategory from being treated as invalidCusvals
  useEffect(() => {
    const newInvalidCusvals = invalidCusvals.filter(
      (item) => !cusvalsFromDeletedCategory.some((i) => i._id === item._id)
    );
    setInvalidCusval(newInvalidCusvals);
  }, [cusvalsFromDeletedCategory]);

  // Check if the cusval has values in a deleted category
  useEffect(() => {
    if (props.initRecordValue) {
      const existsInInitOptions = !!(props.initialOptions || []).find(
        (option) => option.value === props.initRecordValue
      );
      if (!existsInInitOptions) {
        setCusvalsFromDeletedCategory(initialCusval || []);
      }
    }
  }, [props.initRecordValue, props.initialOptions, initialCusval]);

  // changes from local ohsFormSettings updates prop ohsFormSettings
  useEffect(() => {
    if (props.cusvalsConfig) {
      props.ohsFormSettings.useFormMethods.setValue(props.id, {
        ...formValues,
        config: props.cusvalsConfig,
      });

      // update stored values
      if (memoizedCusvalId && !_.isNil(formValues)) {
        setStoredCusvalValues((currVal: any) => ({ ...currVal, [memoizedCusvalId]: formValues }));
      }
    }
  }, [JSON.stringify(formValues), props.cusvalsConfig]);

  // convert array cusval into object compatible to form fields and initialize local ohsFormSettings values once
  useEffect(() => {
    async function formatForm() {
      if (!isFormatted && !_.isNil(props.id)) {
        if (!_.isEmpty(cusvalPropValues) && Array.isArray(cusvalPropValues)) {
          setInitialCusval(cusvalPropValues);

          const formattedCusval = setCusvalValueToForm(cusvalPropValues);
          ohsFormSettings.useFormMethods.setValue(props.id, formattedCusval);
          setIsFormatted(true);
        }
      }
    }
    formatForm();
  }, [isFormatted, cusvalPropValues]);

  // when cusval id change, check if there's a stored value for the new cusvals, if yes, use its values
  useEffect(() => {
    if (memoizedCusvalId) {
      const currentStoredCusval = storedCusvalValues[memoizedCusvalId];
      if (currentStoredCusval) {
        ohsFormSettings.useFormMethods.setValue(props.id, currentStoredCusval);
      }
    }
  }, [memoizedCusvalId]);

  useEffect(() => {
    // if theres initialRecordValue, store the first cusval id
    if (!initialCusvalId && props.initRecordValue && props.cusvalId) {
      setInitialCusvalId(props.cusvalId);
    }
  }, [props.initRecordValue, props.cusvalId]);

  return (
    <Grid
      container
      spacing={
        ohsFormSettings.layout === OhsFormLayoutType.AngularModalForm ||
        ohsFormSettings.layout === OhsFormLayoutType.AngularModalUnderlinedForm
          ? 2
          : 0
      }
      {...props.gridOptions}
    >
      {displayDeletedAndInvalidCusvals()}
      <Box className={classes.container} data-testid={props.id}>
        {(props.cusvalsConfig || []).map((item, index) => returnCusval(item, index))}
      </Box>
    </Grid>
  );
}

OhsCategoryCusvalFieldsSectionContent.defaultProps = {
  gridOptions: {},
  initialOptions: [],
  initRecordValue: undefined,
};
